import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { fetchCategories } from '../../utils/categories';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../utils/roles';
import './Stock.css';

const CreateStockItem = () => {
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get('id');
  const isEditMode = !!itemId;
  
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [initialBalance, setInitialBalance] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const isAdmin = userRole === USER_ROLES.ADMIN;

  useEffect(() => {
    if (!isAdmin) {
      navigate('/stock');
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories from the categories collection
        try {
          const fetchedCategories = await fetchCategories();
          setCategories(fetchedCategories);
        } catch (catError) {
          console.error('Error fetching categories from database:', catError);
          setError('Failed to load categories from database. Please check your connection and refresh the page.');
        }

        if (isEditMode) {
          await loadItemData();
        }
      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load data: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isAdmin, navigate]);

  const loadItemData = async () => {
    try {
      const itemDoc = await getDoc(doc(db, 'stock_items', itemId));
      if (itemDoc.exists()) {
        const data = itemDoc.data();
        // Set the category ID (which will be used as the item name)
        setCategoryId(data.categoryId || '');
        setDescription(data.description || '');
        // For existing items, show currentBalance if it exists, otherwise 0
        setInitialBalance(data.currentBalance?.toString() || '0');
      } else {
        setError('Stock item not found');
      }
    } catch (err) {
      console.error('Error loading item:', err);
      setError('Failed to load stock item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!categoryId) {
      setError('Please select an item');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setSaving(true);

      // Get the selected category name to use as the item name
      const selectedCategory = categories.find(cat => cat.id === categoryId);
      const itemName = selectedCategory ? selectedCategory.name : '';

      const itemData = {
        name: itemName,
        description: description.trim() || null,
        categoryId,
        isActive: true,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser.email
      };

      // Only set currentBalance on create (initial balance), not on edit
      // On edit, balance is managed through expenses
      if (!isEditMode) {
        itemData.currentBalance = initialBalance ? parseFloat(initialBalance) : 0;
        itemData.createdAt = Timestamp.now();
        itemData.createdBy = currentUser.email;
      }

      if (isEditMode) {
        await updateDoc(doc(db, 'stock_items', itemId), itemData);
        setSuccess('Stock item updated successfully!');
      } else {
        await addDoc(collection(db, 'stock_items'), itemData);
        setSuccess('Stock item created successfully!');
      }

      setTimeout(() => {
        navigate('/stock');
      }, 1500);
    } catch (err) {
      console.error('Error saving stock item:', err);
      setError('Failed to save stock item');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="create-stock-item-container">
      <div className="create-stock-item-card">
        <h2>{isEditMode ? 'Edit Stock Item' : 'Create New Stock Item'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="categoryId">Item Name *</label>
            <select
              id="categoryId"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              <option value="" disabled>Select item</option>
              {categories.length > 0 ? (
                categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>No categories available. Please refresh the page.</option>
              )}
            </select>
            {categories.length === 0 && (
              <small className="form-help" style={{ color: '#dc3545' }}>
                No categories found. The system will attempt to create default categories.
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter item description (optional)"
              rows="3"
            />
          </div>

          {!isEditMode && (
            <div className="form-group">
              <label htmlFor="initialBalance">Initial Balance (Optional)</label>
              <input
                type="number"
                id="initialBalance"
                value={initialBalance}
                onChange={(e) => setInitialBalance(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              <small className="form-help">Starting monetary balance for this item. Leave as 0 if starting fresh.</small>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/stock')}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={saving}
            >
              {saving ? 'Saving...' : (isEditMode ? 'Update Item' : 'Create Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateStockItem;

